import clsx from 'clsx';
import {
    Panel as PrimePanel,
    PanelProps as PrimePanelProps,
} from 'primereact/panel';
import { MouseEvent, ReactNode, useEffect, useRef } from 'react';
import { getHeaderTemplate } from './utils/HeaderTemplateTransformations';
import { getHeader } from './utils/HeaderTransformations';

export interface PanelProps extends PrimePanelProps {
    iconOnly?: boolean;
    tooltip?: string;
    headerClassName?: string;
    onlyContent?: boolean;
    tertiary?: boolean;
    invalid?: boolean;
}

export function Panel(props: PanelProps): ReactNode {
    const panelRef = useRef<PrimePanel | null>(null);
    const {
        className: classNameProps,
        toggleable,
        iconOnly: _,
        tooltip: __,
        headerClassName: headerClassNameProps,
        onlyContent,
        tertiary,
        invalid,
        ...defaultProps
    } = props;

    const headerTemplate = getHeaderTemplate(props, panelRef);
    const header = getHeader(props);
    const panelClassName = clsx(
        classNameProps,
        toggleable === false ? 'toggle-disabled' : null,
        onlyContent ? 'content-only' : null,
        tertiary ? 'tertiary' : null,
        invalid ? 'invalid' : null,
    );
    const headerClassName = clsx(headerClassNameProps);

    const setPanelHeight = (): void => {
        if (!panelRef.current) return;
        const panelElement = panelRef.current.getElement();

        if (!panelElement) return;
        const contentElement = panelElement.querySelector(
            '.p-panel-content, .p-toggleable-content',
        );

        if (contentElement) {
            const h = `${contentElement.scrollHeight}px`;
            panelElement.style.setProperty('--panel-h', h);
        }
    };

    // Calculate and set initial height on mount
    useEffect(() => {
        // Set immediately
        setPanelHeight();

        // Also set after a short delay to catch any dynamic content
        const timer = setTimeout(setPanelHeight, 100);

        return () => clearTimeout(timer);
    }, []);

    const handleClick = (e: MouseEvent<HTMLDivElement>): void => {
        if (toggleable === false) {
            return;
        }

        const headerElement = panelRef.current?.getElement()?.firstElementChild;
        const contains = headerElement?.contains(e.target as Node);

        if (contains) {
            panelRef.current?.toggle(e);
        }
    };

    return (
        <PrimePanel
            ref={panelRef}
            pt={{
                header: {
                    className: headerClassName,
                },
            }}
            className={panelClassName}
            headerTemplate={headerTemplate}
            expandIcon="pi pi-chevron-down"
            collapseIcon="pi pi-chevron-up"
            toggleable={toggleable === undefined ? true : toggleable}
            onClick={(e) => handleClick(e)}
            transitionOptions={{
                classNames: {
                    enter: 'panel-transition-enter',
                    enterActive: 'panel-transition-enter--active',
                    enterDone: 'panel-transition--done',
                    exit: 'panel-transition-exit',
                    exitActive: 'panel-transition-exit--active',
                    exitDone: 'panel-transition--done',
                },
                onExit: () => setTimeout(setPanelHeight, 0),
                addEndListener: () => {},
            }}
            {...defaultProps}
            header={header}
        />
    );
}
